import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';

const SearchContainer = styled.div`
  padding: 100px 60px 60px;
`;

const SearchTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 30px;
  color: white;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const ResultCard = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
  }
`;

const ResultImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
`;

const ResultTitle = styled.h3`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent);
  color: white;
  margin: 0;
  font-size: 1rem;
`;

const NoResults = styled.div`
  color: white;
  text-align: center;
  font-size: 1.2rem;
  margin-top: 40px;
`;

const ErrorMessage = styled.div`
  color: #e50914;
  text-align: center;
  font-size: 1.2rem;
  margin-top: 40px;
`;

const TMDB_API_KEY = '6a5272461ae8754ac4c09747a07aab5e'; // Replace with your TMDB API key
const TMDB_BASE_URL = 'https://api.themoviedb.org/3';

const Search = () => {
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const query = searchParams.get('q');

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!query) return;
      
      setLoading(true);
      setError(null);
      try {
        // Search for both movies and TV shows
        const [moviesResponse, tvResponse] = await Promise.all([
          axios.get(`${TMDB_BASE_URL}/search/movie`, {
            params: {
              api_key: TMDB_API_KEY,
              query: query,
              language: 'en-US',
              page: 1,
              include_adult: false
            }
          }),
          axios.get(`${TMDB_BASE_URL}/search/tv`, {
            params: {
              api_key: TMDB_API_KEY,
              query: query,
              language: 'en-US',
              page: 1,
              include_adult: false
            }
          })
        ]);

        // Combine and format results
        const results = [
          ...moviesResponse.data.results.map(movie => ({
            id: movie.id,
            title: movie.title,
            poster_path: movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null,
            type: 'movie'
          })),
          ...tvResponse.data.results.map(show => ({
            id: show.id,
            name: show.name,
            poster_path: show.poster_path ? `https://image.tmdb.org/t/p/w500${show.poster_path}` : null,
            type: 'tv'
          }))
        ];

        setResults(results);
      } catch (error) {
        console.error('Error fetching search results:', error);
        setError('Failed to fetch search results. Please try again later.');
        setResults([]);
      }
      setLoading(false);
    };

    fetchSearchResults();
  }, [query]);

  const handleResultClick = (item) => {
    if (item.type === 'movie') {
      navigate(`/movie/${item.id}`);
    } else {
      navigate(`/series/${item.id}`);
    }
  };

  if (!query) {
    return (
      <SearchContainer>
        <NoResults>Enter a search term to find movies and TV shows</NoResults>
      </SearchContainer>
    );
  }

  return (
    <SearchContainer>
      <SearchTitle>Search Results for "{query}"</SearchTitle>
      {loading ? (
        <NoResults>Loading...</NoResults>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : results.length > 0 ? (
        <ResultsGrid>
          {results.map((item) => (
            <ResultCard 
              key={`${item.type}-${item.id}`}
              onClick={() => handleResultClick(item)}
            >
              <ResultImage
                src={item.poster_path || 'https://via.placeholder.com/500x750?text=No+Image'}
                alt={item.title || item.name}
              />
              <ResultTitle>{item.title || item.name}</ResultTitle>
            </ResultCard>
          ))}
        </ResultsGrid>
      ) : (
        <NoResults>No results found for "{query}"</NoResults>
      )}
    </SearchContainer>
  );
};

export default Search; 